import { useCallback, useState } from "react";

export const FAVOURITE_FILTER_KEY = "divespots.map.favouritesOnly";

/** Off unless this browser turned it on; storage may be missing or throw (private mode). */
export function readFavouriteFilterPref(): boolean {
  try {
    return localStorage.getItem(FAVOURITE_FILTER_KEY) === "on";
  } catch {
    return false;
  }
}

export function writeFavouriteFilterPref(on: boolean): void {
  try {
    if (on) localStorage.setItem(FAVOURITE_FILTER_KEY, "on");
    else localStorage.removeItem(FAVOURITE_FILTER_KEY);
  } catch {
    /* the choice just is not remembered */
  }
}

/** The "Nur Favoriten" switch, remembered per browser (never sent anywhere). */
export function useFavouriteFilterPref(): [boolean, (on: boolean) => void] {
  const [on, setOn] = useState(readFavouriteFilterPref);
  const set = useCallback((v: boolean) => {
    setOn(v);
    writeFavouriteFilterPref(v);
  }, []);
  return [on, set];
}

/**
 * The filter is SHOWN (button and chip) only for a signed-in diver whose favourites query has
 * succeeded and who has at least one favourite. It takes effect only while shown: in every other
 * case (signed out, loading, failed, none left, another account) all pins show, whatever was remembered.
 */
export function favouriteFilterState(input: { ready: boolean; count: number; pref: boolean }): {
  shown: boolean;
  active: boolean;
} {
  const shown = input.ready && input.count > 0;
  return { shown, active: shown && input.pref };
}

/** The pins to draw: the favourites while the filter is active, otherwise every spot. */
export function pinsFor<T extends { id: string }>(
  spots: readonly T[],
  favouriteIds: ReadonlySet<string>,
  active: boolean,
): readonly T[] {
  return active ? spots.filter((s) => favouriteIds.has(s.id)) : spots;
}
